import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ui/ScrollReveal';
import PhoneShowcase from '../components/home/PhoneShowcase';
import { ArrowRight, Wand2, Star, GripVertical, MapPin, Sparkles } from 'lucide-react';

const MapboxGlobe = React.lazy(() => import('../components/home/MapboxGlobe'));

function HomePage() {
  return (
    <div>
      {/* ── HERO + GLOBE (unified full-screen section) ── */}
      <Suspense
        fallback={
          <div className="flex items-center justify-center bg-[#FAF7F2]" style={{ height: '100vh' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400" />
          </div>
        }
      >
        <MapboxGlobe />
      </Suspense>

      {/* ── PHONE SHOWCASE (Apple-style scroll animation) ── */}
      <PhoneShowcase />

      {/* ── TWO PATHS ── */}
      <section className="py-16 bg-[#FAF7F2] overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto items-stretch">

            {/* DIY Builder */}
            <ScrollReveal delay={0}>
              <Link to="/designer/create" className="block h-full group">
                <div className="relative h-full bg-white rounded-3xl border border-platinum-200 shadow-card hover:shadow-card-hover transition-all duration-300 group-hover:scale-[1.02] p-8 flex flex-col overflow-hidden">
                  {/* Coral top accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-coral-400 rounded-t-3xl" />

                  <div className="flex flex-col h-full">
                    {/* Icon + eyebrow */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-coral-50 flex items-center justify-center flex-shrink-0">
                        <Wand2 size={22} className="text-coral-500" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-widest text-coral-400">Build it yourself</span>
                    </div>

                    {/* Text */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-black text-charcoal-500 leading-tight mb-3">
                        Your itinerary,<br />built by AI
                      </h3>
                      <p className="text-platinum-600 text-base leading-relaxed mb-6">
                        Our AI builds your full day-by-day itinerary in seconds — then drag, edit and make it feel completely yours. Free to start.
                      </p>
                    </div>

                    {/* Mini UI preview */}
                    <div className="bg-platinum-50 rounded-2xl p-3 mb-6 space-y-2 border border-platinum-100">
                      {[
                        { icon: <GripVertical size={12} className="text-platinum-400" />, label: 'Morning · Arrive in Kyoto', color: 'bg-columbia-100', dot: 'bg-columbia-400' },
                        { icon: <GripVertical size={12} className="text-platinum-400" />, label: 'Afternoon · Fushimi Inari', color: 'bg-coral-50', dot: 'bg-coral-400' },
                        { icon: <GripVertical size={12} className="text-platinum-400" />, label: 'Evening · Gion dinner', color: 'bg-naples-50', dot: 'bg-naples-500' },
                      ].map((row, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-platinum-100">
                          {row.icon}
                          <div className={`w-5 h-5 rounded-lg flex items-center justify-center ${row.color}`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${row.dot}`} />
                          </div>
                          <span className="text-xs text-charcoal-400 font-medium">{row.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-coral-400 bg-coral-50 px-3 py-1.5 rounded-xl">Free to start</span>
                      <span className="w-10 h-10 rounded-full bg-coral-400 flex items-center justify-center transition-all duration-300 group-hover:translate-x-1 group-hover:bg-coral-500">
                        <ArrowRight size={17} className="text-white" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </ScrollReveal>

            {/* Atlas Plan */}
            <ScrollReveal delay={0.1}>
              <Link to="/atlas-plan" className="block h-full group">
                <div className="relative h-full bg-white rounded-3xl border border-platinum-200 shadow-card hover:shadow-card-hover transition-all duration-300 group-hover:scale-[1.02] p-8 flex flex-col overflow-hidden">
                  {/* Columbia top accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-columbia-500 rounded-t-3xl" />

                  <div className="flex flex-col h-full">
                    {/* Icon + eyebrow */}
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-2xl bg-columbia-50 flex items-center justify-center flex-shrink-0">
                        <Star size={22} className="text-columbia-600" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-widest text-columbia-500">Done for you</span>
                    </div>

                    {/* Text */}
                    <div className="flex-1">
                      <h3 className="text-2xl font-black text-charcoal-500 leading-tight mb-3">
                        Atlas Plan —<br />planned for you
                      </h3>
                      <p className="text-platinum-600 text-base leading-relaxed mb-6">
                        AI drafts your day-by-day plan in minutes. A travel professional checks it line by line and makes it 100% personal to you — the best itinerary based on your preferences. You book it yourself.
                      </p>
                    </div>

                    {/* Feature list */}
                    <div className="bg-platinum-50 rounded-2xl p-3 mb-6 space-y-2 border border-platinum-100">
                      {[
                        { icon: <Sparkles size={12} className="text-columbia-500" />, label: 'AI-drafted, checked by a travel pro' },
                        { icon: <Star size={12} className="text-columbia-500" />, label: 'Day order, pacing, what to cut' },
                        { icon: <MapPin size={12} className="text-columbia-500" />, label: 'Fixed, transparent pricing' },
                      ].map((row, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-platinum-100">
                          <div className="w-5 h-5 rounded-lg flex items-center justify-center bg-columbia-50">
                            {row.icon}
                          </div>
                          <span className="text-xs text-charcoal-400 font-medium">{row.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-columbia-600 bg-columbia-50 px-3 py-1.5 rounded-xl">Let's go</span>
                      <span className="w-10 h-10 rounded-full bg-columbia-500 flex items-center justify-center transition-all duration-300 group-hover:translate-x-1 group-hover:bg-columbia-600">
                        <ArrowRight size={17} className="text-white" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </ScrollReveal>

          </div>
        </div>
      </section>

    </div>
  );
}

export default HomePage;
